export function formatTimeZoneAbbreviation(timeZone: string | undefined, locale: string, at: Date): string | undefined {
    if (!timeZone) {
        return undefined;
    }

    try {
        return new Intl.DateTimeFormat(locale || undefined, { hour: "numeric", timeZoneName: "short", timeZone }).formatToParts(at).find(part => part.type === "timeZoneName")?.value;
    } catch {
        return undefined;
    }
}

export function formatLocalTime(timeZone: string | undefined, locale: string, now: Date): string | undefined {
    if (!timeZone) {
        return undefined;
    }

    try {
        const clock = new Intl.DateTimeFormat(locale || undefined, { hour: "numeric", minute: "2-digit", timeZone }).format(now);
        const zoneAbbreviation = formatTimeZoneAbbreviation(timeZone, locale, now);

        return zoneAbbreviation ? `${clock} (${zoneAbbreviation})` : clock;
    } catch {
        return undefined;
    }
}

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

export function getDateTimeFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
    const key = `${locale}${JSON.stringify(options)}`;
    let formatter = dateTimeFormatters.get(key);

    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, options);
        dateTimeFormatters.set(key, formatter);
    }

    return formatter;
}

function isUsableTimeZone(timeZone: string): boolean {
    try {
        return Intl.DateTimeFormat(undefined, { timeZone }).resolvedOptions().timeZone.length > 0;
    } catch {
        return false;
    }
}

export function resolveTimeZone(sessionTimeZone: string | undefined): string {
    if (sessionTimeZone && isUsableTimeZone(sessionTimeZone)) {
        return sessionTimeZone;
    }

    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function parseUtcDate(value: string | undefined): Date | undefined {
    if (!value) {
        return undefined;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? undefined : date;
}

export function formatUtcDate(date: Date, locale: string): string {
    return new Intl.DateTimeFormat(locale || undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(date);
}

export function formatUtcLongDate(value: string | undefined, locale: string): string | undefined {
    const date = parseUtcDate(value);

    return date && formatUtcDate(date, locale);
}

export function formatUtcShortDate(value: string | undefined, locale: string): string | undefined {
    const date = parseUtcDate(value);

    return date && new Intl.DateTimeFormat(locale || undefined, { month: "short", day: "numeric", timeZone: "UTC" }).format(date);
}

export function formatUtcContextualDate(value: string | undefined, locale: string, now: Date): string | undefined {
    const date = parseUtcDate(value);

    if (!date) {
        return undefined;
    }

    return date.getUTCFullYear() === now.getUTCFullYear() ? formatUtcShortDate(value, locale) : formatUtcLongDate(value, locale);
}

export function formatUtcMonthYear(value: string | undefined, locale: string): string | undefined {
    const date = parseUtcDate(value);

    return date && new Intl.DateTimeFormat(locale || undefined, { month: "short", year: "numeric", timeZone: "UTC" }).format(date);
}

export function calculateTenureYears(hireDate: string | undefined, now: Date): number | undefined {
    const hired = parseUtcDate(hireDate);

    if (!hired) {
        return undefined;
    }

    let years = now.getUTCFullYear() - hired.getUTCFullYear();
    const monthDiff = now.getUTCMonth() - hired.getUTCMonth();

    if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < hired.getUTCDate())) {
        years -= 1;
    }

    return Math.max(years, 0);
}

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;

export function formatTimeAgo(at: string | undefined, locale: string, now: number = Date.now()): string | undefined {
    if (!at) {
        return undefined;
    }

    const elapsedMs = now - new Date(at).getTime();
    if (Number.isNaN(elapsedMs)) {
        return undefined;
    }

    const relative = new Intl.RelativeTimeFormat(locale || undefined, { numeric: "auto" });

    const days = Math.floor(elapsedMs / MS_PER_DAY);
    if (days >= 1) {
        return relative.format(-days, "day");
    }

    const hours = Math.floor(elapsedMs / MS_PER_HOUR);
    if (hours >= 1) {
        return relative.format(-hours, "hour");
    }

    return relative.format(-Math.max(1, Math.floor(elapsedMs / MS_PER_MINUTE)), "minute");
}
